<script setup lang="ts">
    import type { Table } from '@tanstack/vue-table';
    import { computed, watch } from 'vue';
    import {
        Accordion,
        AccordionContent,
        AccordionItem,
        AccordionTrigger,
    } from '@/components/ui/accordion';
    import { Button } from '@/components/ui/button';
    import { PopoverRoot, PopoverContent, PopoverTrigger } from 'reka-ui';
    import { Funnel } from 'lucide-vue-next';
    import { useI18n } from 'vue-i18n';
    import ColumnFilter from '@/components/filters/ColumnFilter.vue';

    const props = defineProps<{
        table: Table<any>;
    }>();

    const { t } = useI18n();

    const columns = computed(() =>
        props.table
            .getAllLeafColumns()
            .filter((column) => typeof column.accessorFn !== 'undefined' && column.getCanFilter()),
    );

    const filterCount = computed(() => {
        return props.table.getState().columnFilters.length;
    });

    const getColumnTitle = (column: any) => {
        const headerContext = { column };
        try {
            const headerContent = column.columnDef.header(headerContext);
            return headerContent?.props?.title || column.id;
        } catch (error) {
            console.error('Filterable column without header:', column.id);
            return column.id;
        }
    };
</script>

<template>
    <PopoverRoot>
        <PopoverTrigger as-child>
            <Button
                variant="outline"
                size="xs"
                class="relative"
            >
                <Funnel />
                <Transition name="fade-scale">
                    <span
                        v-if="filterCount > 0"
                        class="bg-primary absolute -top-1 -right-1 flex size-4 items-center justify-center rounded-full text-[10px] leading-none text-white"
                    >
                        {{ filterCount }}
                    </span>
                </Transition>
            </Button>
        </PopoverTrigger>
        <PopoverContent
            align="end"
            class="bg-popover text-popover-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 max-h-(--reka-popover-content-available-height) min-w-xs origin-(--reka-popover-content-transform-origin) overflow-x-hidden overflow-y-auto rounded-md border p-1 shadow-md"
            :sideOffset="4"
        >
            <div class="flex items-center justify-between px-2">
                <span class="text-sm font-medium">{{ t('tables.filters.filters') }}</span>
                <Button
                    variant="link_destructive"
                    size="xs"
                    width="narrow"
                    @click="table.resetColumnFilters()"
                >
                    {{ t('tables.reset') }}
                </Button>
            </div>
            <Accordion
                type="single"
                collapsible
            >
                <AccordionItem
                    v-for="column in columns"
                    :key="column.id"
                    :value="column.id"
                >
                    <AccordionTrigger>
                        <div class="flex items-center gap-2">
                            {{ t(getColumnTitle(column)) }}
                            <span
                                v-if="column.getFilterValue()"
                                class="bg-primary size-1 rounded-full"
                            />
                        </div>
                    </AccordionTrigger>
                    <AccordionContent class="p-2">
                        <ColumnFilter
                            :column="column"
                            :table="table"
                        />
                    </AccordionContent>
                </AccordionItem>
            </Accordion>
        </PopoverContent>
    </PopoverRoot>
</template>
